import Head from 'next/head';
import type { ReactElement, ReactNode } from 'react';

import CncRfqCtaBanner from '@/components/CncRfq/CncRfqCtaBanner';
import CncRfqFaq from '@/components/CncRfq/CncRfqFaq';
import CncRfqFeatureGrid from '@/components/CncRfq/CncRfqFeatureGrid';
import CncRfqFeatureSpotlights from '@/components/CncRfq/CncRfqFeatureSpotlights';
import CncRfqHero from '@/components/CncRfq/CncRfqHero';
import CncRfqHowItWorks from '@/components/CncRfq/CncRfqHowItWorks';
import CncRfqMetrics from '@/components/CncRfq/CncRfqMetrics';
import CncRfqProblems from '@/components/CncRfq/CncRfqProblems';
import CncRfqQuotePreview from '@/components/CncRfq/CncRfqQuotePreview';
import { AnimatedButton } from '@/components/Landing/AnimatedButton';
import ContactFormSheet from '@/components/Landing/ContactFormSheet';
import Layout from '@/components/Landing/Layout';
import OurClientsMarquee from '@/components/Landing/OurClientsMarquee';
import Opinions from '@/components/OurSuccessStories/Opinions';
import Text from '@/components/Text';
import { createMetaTags, JsonLd } from '@/utils/seo';
import { smoothScroll } from '@/utils/smoothScroll';

import type { NextPageWithLayout } from '../_app';

const CncRfq: NextPageWithLayout = () => {
  return (
    <>
      <style jsx global>
        {`
          body {
            background: #000000;
          }
        `}
      </style>
      <Head>
        {createMetaTags({
          description:
            'Upload a DXF, STEP, or shop-drawing PDF and get an instant CNC laser-cutting quote — powered by MindPal.',
          title: 'CNC Instant Quote — MindPal',
        })}
        <JsonLd
          key="CncRfqService"
          data={{
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: 'MindPal CNC Instant Quote',
            url: 'https://mindpal.co/cnc-rfq',
            provider: { '@id': 'https://mindpal.co/#organization' },
            description:
              'AI-powered instant quoting for CNC and sheet-metal shops. Upload a CAD file and get an itemized, editable quote in seconds.',
            serviceType: ['CNC Quoting', 'Manufacturing Quote Automation'],
            areaServed: 'Worldwide',
          }}
        />
      </Head>

      <CncRfqHero />

      <DarkSection>
        <OurClientsMarquee />
      </DarkSection>
      <DarkSection>
        <CncRfqMetrics />
      </DarkSection>
      <DarkSection>
        <CncRfqProblems />
      </DarkSection>

      <LightSection>
        <CncRfqHowItWorks />
      </LightSection>
      <LightSection>
        <CncRfqQuotePreview />
      </LightSection>

      <DarkSection>
        <CncRfqFeatureSpotlights />
      </DarkSection>
      <DarkSection>
        <CncRfqFeatureGrid />
      </DarkSection>
      <DarkSection>
        <div className="flex w-full flex-col items-center justify-center gap-1">
          <Text variant="display-4" className="text-center text-jb-neutral-100">
            Real stories of collaboration and success
          </Text>
          <Text className="pb-3 text-center text-jb-neutral-600">
            From startups to global brands, our partners show how
            <br /> MindPal turns challenges into results.
          </Text>
          <ContactFormSheet
            sheetTriggerProps={{ asChild: true, className: 'self-center' }}
          >
            <AnimatedButton
              label="Get started"
              icon="material-symbols:arrow-back"
            />
          </ContactFormSheet>
        </div>
        <Opinions className="w-full pt-16" />
      </DarkSection>

      <LightSection>
        <CncRfqCtaBanner
          title="Want a Custom AI Engine Built for Your Business?"
          description="This CNC Quoter is just one example of what MindPal can build for your company. We craft bespoke AI agents and workflows customized to your internal formulas, ERP integrations, and business processes."
          onPrimaryClick={() => smoothScroll('#cnc-rfq-upload')}
        />
      </LightSection>
      <LightSection>
        <CncRfqFaq />
      </LightSection>
      <LightSection>
        <CncRfqCtaBanner
          title="Try it yourself: upload a part and see"
          description="It takes about 30 seconds and no sign-up. The best way to judge what MindPal builds is to put a real part through it."
          onPrimaryClick={() => smoothScroll('#cnc-rfq-upload')}
        />
      </LightSection>
    </>
  );
};

const DarkSection = ({ children }: { children: ReactNode }) => (
  <section className="w-full bg-black">
    <div className="mx-auto max-w-screen-3xl px-5 py-16 md:p-20">
      {children}
    </div>
  </section>
);

const LightSection = ({ children }: { children: ReactNode }) => (
  <section className="w-full bg-jb-neutral-200">
    <div className="mx-auto max-w-screen-3xl px-5 py-16 md:p-20">
      {children}
    </div>
  </section>
);

CncRfq.getLayout = (page: ReactElement) => <Layout>{page}</Layout>;

export default CncRfq;
